import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Search } from 'lucide-react';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Pagination from '../../components/Pagination';
import { listWorkflows, publishWorkflow, pauseWorkflow, resumeWorkflow, archiveWorkflow } from '../../utils/queries';
import { humanizeEventType } from '../../utils/commerceEventTypes';

const PAGE_SIZE = 20;

const STATUS_LABEL = { DRAFT: 'Draft', ACTIVE: 'Active', PAUSED: 'Paused', ARCHIVED: 'Archived' };

export default function WorkflowsList() {
	const { activeStoreId } = useContext(MyContext);
	const [workflows, setWorkflows] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');

	const refresh = () => {
		if (!activeStoreId) return;
		setLoading(true);
		listWorkflows({ storeId: activeStoreId, limit: PAGE_SIZE, offset: page * PAGE_SIZE, search: search || undefined }).then((result) => {
			if (result?.success) {
				setWorkflows(result.data.workflows);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, search, activeStoreId]);

	const runAction = async (fn, row, successMessage) => {
		const result = await fn(row.workflow_definition_id, activeStoreId);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to update workflow.');
			return;
		}
		toast.success(successMessage);
		refresh();
	};

	const columns = [
		{
			key: 'name',
			header: 'Workflow',
			render: (value, row) => (
				<Link to={`/workflows/${row.workflow_definition_id}`} className='text-black font-medium hover:underline'>
					{value}
				</Link>
			),
		},
		{
			key: 'trigger_event_type',
			header: 'Trigger',
			render: (value) => (value ? humanizeEventType(value) : '—'),
		},
		{
			key: 'status',
			header: 'Status',
			render: (value) => STATUS_LABEL[value] || value,
		},
		{ key: 'version', header: 'Version', render: (value) => value ?? '—' },
		{
			key: 'last_run_at',
			header: 'Last Run',
			render: (value) => (value ? new Date(value).toLocaleString() : 'Never'),
		},
		{ key: 'run_count', header: 'Runs', align: 'right' },
		{
			key: 'workflow_definition_id',
			header: '',
			sortable: false,
			render: (_value, row) => (
				<div className='flex gap-2 justify-end'>
					{row.status === 'ACTIVE' && (
						<button type='button' className='btn-outline-small' onClick={() => runAction(pauseWorkflow, row, 'Workflow paused.')}>
							Pause
						</button>
					)}
					{row.status === 'PAUSED' && (
						<button type='button' className='btn-secondary-small' onClick={() => runAction(resumeWorkflow, row, 'Workflow activated.')}>
							Activate
						</button>
					)}
					{row.status === 'DRAFT' && (
						<button type='button' className='btn-primary-small' onClick={() => runAction(publishWorkflow, row, 'Workflow published.')}>
							&nbsp;Publish&nbsp;
						</button>
					)}
					{row.status !== 'ARCHIVED' && (
						<button type='button' className='btn-secondary-small' onClick={() => runAction(archiveWorkflow, row, 'Workflow archived.')}>
							Archive
						</button>
					)}
				</div>
			),
		},
	];

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-start justify-between mb-6'>
				<div>
					<h1 className='text-2xl font-light tracking-tight'>Workflows</h1>
					<p className='text-sm text-gray-500'>Automate actions in response to commerce events.</p>
				</div>
				<Link to='/workflows/new' className='btn-primary pt-1'>
					Create Workflow
				</Link>
			</div>

			<div className='mb-5'>
				<label>Search</label>
				<div className='relative max-w-sm'>
					<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
					<input
						className='w-52 form-input pl-9'
						placeholder='Workflow name'
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(0);
						}}
					/>
				</div>
			</div>

			<DataTable columns={columns} data={workflows} loading={loading} emptyHeading='No workflows yet' emptyMessage='Create one to automate actions on commerce events.' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />
		</div>
	);
}
